import type { Campaign } from 'core/domains';
import type { BrazilianStates } from 'config';

import type { ResponseWithData } from 'core/services/bff/@types/utilities';
import type { Discount } from 'core/services/bff/@types/cart';

// Abstract Types
interface Identifier {
  id: number | string;
  name: string;
}

interface IdentifierWithSlug extends Identifier {
  slugName?: string;
}

interface BaseProduct {
  aggregatedSkus: string[];
  badge: string[];
  breadcrumb: Breadcrumb[];
  compositionId: string;
  gift: boolean;
  gtin: string;
  hasGiftBenefits: boolean;
  imageObjects: ImageObject[];
  name: string;
  organization: IdentifierWithSlug;
  partnerSku: string;
  releaseDate: string;
  resellerOnly: boolean;
  sku: string;
  slugName: string;
  total: number;
}

// Private Types

interface AbcAnalysis {
  abcCategory: string;
  abcCategoryInDepartment: string;
  abcCategoryInBrand: string;
}

interface AggregateRating {
  ratingValue: number;
  ratingCount: number;
}

interface Attribute extends IdentifierWithSlug {
  required: boolean;
  filterable: boolean;
  groupUpdate: boolean;
  values: Value[];
}

interface Brand extends IdentifierWithSlug {
  line?: Line;
  imageObject?: BrandLogo;
  id?: number;
  name?: string;
  slugName?: string;
}

interface BrandLogo {
  logo: string;
}

interface Breadcrumb {
  name?: string;
  url?: string;
}

interface Category extends IdentifierWithSlug {
  active?: boolean;
  attributes?: Attribute[];
  id?: string;
  name?: string;
  subcategories?: Category[];
  subcategory?: Category;
}

interface Department extends IdentifierWithSlug {
  categories: Category[];
  category: Category;
  id: string;
  name: string;
  priority: number;
}

export interface Details {
  advice: string;
  description: string;
  occasion: string;
  olfactivePyramid: OlfactivePyramid;
  result: string;
  shortDescription: string;
}

interface ExternalCategories {
  id: string;
  link: Link;
  breadCrumb: string;
}

interface Facet {
  type: string;
  title: string;
  field: string;
  entries: FacetEntry[];
  totalDocCount: number;
  postFilteredTotalDocCount: number;
}

interface FacetEntry {
  name: string;
  slugName: string;
  docCount: number;
  postFilteredDocCount: number;
  selected: boolean;
  facet?: Facet;
  hasResults: boolean;
}

export interface ImageObject {
  featured: boolean;
  thumbnail: string;
  small: string;
  medium: string;
  large: string;
  extraLarge: string;
  imageUrl: string;
  sample?: string;
  fileName?: string;
}

interface Integration {
  externalCategories: ExternalCategories;
  name: string;
  source: string;
}

interface InventoryStatus {
  available: boolean;
  qtd: number;
}

interface Kit {
  kits?: KitProduct[];
  total: number;
  badge: string[];
  breadcrumb: Breadcrumb[];
  resellerOnly: boolean;
  hasGiftBenefits: boolean;
}

type KitProduct = Pick<BaseProduct, 'sku', 'name', 'imageObjects'> & {
  consumerPrice: ConsumerPrice;
  resellerPrice: ResellerPrice;
};

interface LastUpdate {
  updatedById: string;
  updatedByEmail: string;
  updateAt: string;
}

type Line = Omit<IdentifierWithSlug, 'id'> & {
  id?: number;
  name?: string;
  slugName?: string;
  description?: string;
};

interface Link {
  href: string;
}

interface Marketable {
  isMarketable: boolean;
  hasDescription: boolean;
  isGift: boolean;
  discontinued: boolean;
  hasImage: boolean;
  soldSeparately?: boolean;
  hasCycles: boolean;
  hasCategories: boolean;
  isResellerProduct: boolean;
}

interface MediaObject {
  contentUrl: string;
}

interface OlfactivePyramid {
  topNodes?: string;
  middleNodes?: string;
  baseNodes?: string;
}

interface Presentation {
  value: string;
  unit: string;
}

interface PriceClass {
  priceClass: string;
}

interface ProductLabel {
  id: string;
  label: string;
  organization: string;
  link?: string;
  beginAt: string;
  endAt: string;
  imageUri?: string;
}

interface Region {
  states: BrazilianStates;
  publishedOnMagazine: boolean;
  priceSpecification: PriceSpecification;
  resellerPriceSpecification: ResellerPriceSpecification;
}

interface Sales {
  firstSale: string;
  sales: number;
  salesLast2Years: number;
  salesLast3Months: number;
  salesLast3Hours: number;
  salesPercentage: number;
  salesPercentageLast2Years: number;
  salesPercentageLast3Months: number;
  salesPercentageLast3Hours: number;
  salesPercentageLast3MonthsByDepartment: number;
  salesPercentageLast3MonthsByBrand: number;
}

interface Seo {
  title: string;
  keywords?: string;
  description: string;
}

interface Statistics {
  abcAnalysis: AbcAnalysis;
  brand: Brand;
  price: PriceClass;
  sales: Sales;
}

interface Value extends IdentifierWithSlug {
  checked: boolean;
  disabled: boolean;
  value: string;
}

interface ConsumerPrice {
  price: number;
  maxPrice: number;
  percent: number;
  discount: number;
}

interface ResellerPrice {
  profit: number;
  price: number;
  maxPrice?: number;
  discountPercentage?: number;
  discount?: number;
}

interface PriceWrapper {
  consumerPrice: ConsumerPrice;
  resellerPrice: ResellerPrice;
}

interface Product extends BaseProduct {
  aggregateRating: AggregateRating;
  blzSku: string;
  brand: Brand;
  cycles: Cycle[];
  departments: Department[];
  details: Details;
  featured: boolean;
  gtins: string[];
  hasSimilar: boolean;
  hiddenInCart: boolean;
  imageObjects: ImageObject[];
  integrations: Integration[];
  inventory: Inventory;
  kit: Kit;
  lastUpdate: LastUpdate;
  lastUpdateElasticsearch: string;
  marketable: Marketable;
  mediaObject: MediaObject;
  novelty: boolean;
  presentation: Presentation;
  productLabel?: ProductLabel;
  productType?: string;
  resellerOnly: boolean;
  seo: Seo;
  soldSeparately: boolean;
  statistics: Statistics;
  surprise: boolean;
  versions: string[];
  priceWrapper?: PriceWrapper;
}

interface Cycle {
  id: string;
  regions: Region[];
  profitabilityProductType: number;
}

interface ImageObject {
  featured: boolean;
  thumbnail: string;
  small: string;
  medium: string;
  large: string;
  extraLarge: string;
  imageUrl: string;
  sample?: string;
  fileName?: string;
}

export interface Inventory {
  managed: InventoryStatus;
  unmanaged: InventoryStatus;
}

export interface PriceSpecification {
  price: number;
  maxPrice: number;
  percent: number;
  discount: number;
}

export interface ResellerPriceSpecification {
  profit: number;
  price: number;
}

// Root Response Type

interface RewardPayload {
  discounts: Discount[];
  gifts: Gifts[];
}

interface ProductPayload {
  facets: Facet[];
  inventorySuccess: boolean;
  items: Product[];
}

interface ProductsKit {
  available: boolean;
  imageObjects: ImageObject[];
  name: string;
  quantity: number;
  quantityPerKit?: number;
  sampleImage?: string;
  sku: string;
}

interface ProductsKitChoice {
  items: ProductsKit[];
  productStructureDescription: string;
  quantityPerKit: number;
  structureCode: number;
}

interface ComboPayload {
  available: boolean;
  bannerImage: string;
  description: string;
  imageObjects: ImageObject[];
  message: string;
  name: string;
  priceSpecification: PriceSpecification;
  productsKit: ProductsKit[];
  productsKitChoice: ProductsKitChoice[];
  publishedOnMagazine: boolean;
  quantity: number;
  resellerPriceSpecification: resellerPriceSpecification;
  sku: string;
}

export type ProductRequestOptions = {
  availability?: string;
  cycle?: number;
  department?: string;
  limit?: number;
  page?: number;
  sku?: string;
  text?: string;
  sort?: string;
};

export type AvailableShowcases = {
  name: string;
  description: string;
  tag: string;
  order: number;
  multiBrand: boolean;
  isFetched: boolean;
};

export type ProductResponse = ResponseWithData<ProductPayload>;

export type ComboResponse = ResponseWithData<ComboPayload>;

export type CampaignResponse = ResponseWithData<Omit<Campaign['data'], 'hasRequiredPoints'>>;

export const enum ProfitabilityProductType {
  CONSUMER = 1,
  RESELLER = 2,
}

export type AvailableShowcasesResponse = {
  data: AvailableShowcases[];
};
